import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { Column, SortingRule } from 'react-table';

import { UserTeamAndRoleManagement } from '../../../../../../../../shared/organizations/types';
import { useHasPermissionForTeamMembers } from '../../../../../../../helpers/permissions';
import { Action } from '../../../../../../../shared/permissions';
import { Pill } from '../../../../../../generic';
import SimpleReactTable from '../../../../../../generic/SimpleReactTable';
import TableHeader from '../../../../../../generic/TableHeader';
import SettingsPageTableActions from '../../../../../../shared/settings/SettingsPageTableActions';
import useUserRoleManagement from '../../../../hooks/useUserRoleManagement';
import type { TeamGroupUser } from '../../../../../../../../shared/types/routes/teamGroups';

import AddTeamGroupMemberModal from './AddTeamGroupMemberModal';
import TeamGroupMembersTableRemove from './TeamGroupMembersTableRemove';
import TeamGroupMembersTableRoleDropdown from './TeamGroupMembersTableRoleDropdown';

import styles from '../../../management/TeamMembersTable.module.less';

const defaultSortMode: [SortingRule] = [
  {
    id: 'name',
    desc: false,
  },
];

const useTeamGroupMembersColumns = (
  sortMode: SortingRule,
  teamGroupId: number,
  isTeamRoleIDPManaged: boolean,
  isTeamMembershipIDPManaged: boolean,
): Array<Column<TeamGroupUser>> =>
  useMemo(
    () => [
      {
        id: 'username',
        Header: <TableHeader title="Name" sort={{ id: 'username', currentMode: sortMode }} />,
        accessor: 'username',
        resizable: false,
        width: 350,
        style: {
          textOverflow: 'ellipsis',
        },
        Cell: ({ original }) => (
          <span className={styles.username}>
            {original.username} {original.isServiceAccount && <Pill color="blue">Service Account</Pill>}
          </span>
        ),
      },
      {
        id: 'email',
        Header: <TableHeader title="Email" sort={{ id: 'email', currentMode: sortMode }} />,
        accessor: 'email',
        resizable: false,
        width: 350,
        style: {
          textOverflow: 'ellipsis',
        },
      },
      {
        id: 'role',
        Header: <TableHeader title="Role" sort={{ id: 'role', currentMode: sortMode }} />,
        accessor: 'roleId',
        resizable: false,
        Cell: ({ original }) => (
          <TeamGroupMembersTableRoleDropdown
            roleId={original?.roleId}
            userId={original?.userId}
            teamGroupId={teamGroupId}
            isDisabled={isTeamRoleIDPManaged}
          />
        ),
        style: { overflow: 'visible' },
      },
      {
        id: 'remove',
        Header: <TableHeader title="Remove" />,
        resizable: false,
        width: 70,
        Cell: ({ original }) => (
          <TeamGroupMembersTableRemove
            userId={original?.userId}
            teamGroupId={teamGroupId}
            isDisabled={isTeamMembershipIDPManaged}
          />
        ),
      },
    ],
    [sortMode, teamGroupId, isTeamRoleIDPManaged, isTeamMembershipIDPManaged],
  );

type Props = {
  isLoading: boolean;
  teamGroupId: number;
  teamGroupName: string;
  members: TeamGroupUser[];
};

/**
 * Displays the members of a team group, along with
 * the ability to search, sort and add new members.
 */
const TeamGroupMembersTable: VoidFunctionComponent<Props> = ({ members, isLoading, teamGroupId, teamGroupName }) => {
  const hasPermissionForTeamMembers = useHasPermissionForTeamMembers();

  const { data: userRoleManagement } = useUserRoleManagement();
  const isTeamRoleIDPManaged = userRoleManagement?.teams === UserTeamAndRoleManagement.IDP;
  const isTeamMembershipIDPManaged =
    userRoleManagement?.teams === UserTeamAndRoleManagement.IDP ||
    userRoleManagement?.teams === UserTeamAndRoleManagement.IDP_TEAMS_FOSSA_ROLES;
  const canAddMembers = hasPermissionForTeamMembers(Action.Edit, teamGroupId) && !isTeamMembershipIDPManaged;

  const [sortMode, setSortMode] = useState<SortingRule>(defaultSortMode[0]);
  const columns = useTeamGroupMembersColumns(sortMode, teamGroupId, isTeamRoleIDPManaged, isTeamMembershipIDPManaged);
  const [search, setSearch] = useState('');
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);

  const searchLowerCase = search.toLowerCase();
  const filteredMembers = useMemo(
    () => members.filter((member) => member.username?.toLowerCase().includes(searchLowerCase)),
    [members, searchLowerCase],
  );

  return (
    <>
      <SettingsPageTableActions>
        <SettingsPageTableActions.TooltipButton
          id="add-member-to-team-group-button"
          size="large"
          type="secondary"
          onClick={() => setIsAddMemberModalOpen(true)}
          isDisabled={!canAddMembers}
          showTooltip={!canAddMembers}
          tooltip="You are not permitted to add members to this team group."
        >
          Add Member
        </SettingsPageTableActions.TooltipButton>
        <SettingsPageTableActions.SearchInput
          label="Search team group members"
          value={search}
          onChange={setSearch}
          placeholder="Search team group members"
          size="medium"
        />
      </SettingsPageTableActions>
      <SimpleReactTable
        data={filteredMembers}
        columns={columns}
        defaultSortMode={defaultSortMode}
        onSortedChanged={setSortMode}
        showPagination={filteredMembers.length > 20}
        cacheId="organizationTeamGroupMembers"
        isLoading={isLoading}
      />
      <AddTeamGroupMemberModal
        isOpen={isAddMemberModalOpen}
        onHide={() => setIsAddMemberModalOpen(false)}
        teamGroupId={teamGroupId}
        teamGroupName={teamGroupName}
      />
    </>
  );
};

export default TeamGroupMembersTable;
